import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';

import { ClientService, QuotaService, SessionService } from '~app-client/core/services';
import { HomeService } from '~app-client/home/services';
import { SidenavService } from '~app-client-item/home/services/sidenav.service';
import { BookmarksService } from '~app-client-item/integrations/_internal/add-new/modules/browser-plugin/services/bookmarks.service';
import { BookmarksState } from '~app-client-item/integrations/_internal/add-new/modules/browser-plugin/types';
import { getBrowser } from '~app-client-item/integrations/_internal/add-new/modules/browser-plugin/utils';
import { ProgressLineModule } from '~app-ui/progress/line';
import { ReachUserThumbnailModule } from '~app-ui/thumbnails/users';
import { UiNewButtons } from '~app-ui-new/buttons';
import { UiNewLayouts } from '~app-ui-new/layout';
import { COMMON_CLIENT_CONFIG } from '~ng-common-lib/core/tokens/client-config';
import { TooltipsModule } from '~ng-common-lib/ui/tooltips';

import { detectBrowser, reachBrowserName } from '../open-mobile-menu/utils';

const CHROME_PATH =
	'https://chrome.google.com/webstore/detail/reach-web-clipper/kegfimibbdadlkgbiijhljlolgfndcem';
const FIREFOX_PATH = 'https://addons.mozilla.org/en-US/firefox/addon/reach-web-saver/';

@Component({
	selector: 'sidenav-header',
	templateUrl: 'sidenav-header.component.html',
	styleUrls: ['sidenav-header.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslocoModule,
		UiNewButtons,
		UiNewLayouts,
		ReachUserThumbnailModule,
		ProgressLineModule,
		TooltipsModule,
	],
	providers: [
		BookmarksService,
		{
			provide: TRANSLOCO_SCOPE,
			useValue: { scope: 'profile' },
			multi: true,
		},
		{
			provide: TRANSLOCO_SCOPE,
			useValue: { scope: 'integrations/browser-plugin' },
			multi: true,
		},
	],
	host: {
		class: 'block',
	},
})
export class SidenavHeaderComponent implements OnInit {
	public readonly sidenavService = inject(SidenavService);
	public readonly homeService = inject(HomeService);
	public readonly isDesktop$ = inject(ClientService).isDesktop$;
	private readonly bookmarksService = inject(BookmarksService);
	private readonly sessionService = inject(SessionService);
	private readonly router = inject(Router);
	private readonly quotaService = inject(QuotaService);
	private readonly clientConfig = inject(COMMON_CLIENT_CONFIG);

	private readonly hasPlugin$$ = new BehaviorSubject(false);
	public readonly hasPlugin$ = this.hasPlugin$$.asObservable();

	public readonly user$ = this.sessionService.user$.pipe(
		filter((user) => {
			return !!user;
		})
	);

	public readonly quota$ = this.quotaService.quotas$;

	public readonly browserName = Capacitor.isNativePlatform()
		? null
		: reachBrowserName[detectBrowser()];

	public readonly appTitle = this.clientConfig.branding.appTitle;
	public readonly useIconWithTitleInSideNav =
		this.clientConfig.branding.sideNavLogoVariant === 'icon-with-title';

	private readonly browserForPlugin = getBrowser();

	public readonly pluginPath =
		this.browserForPlugin === 'chrome'
			? CHROME_PATH
			: this.browserForPlugin === 'firefox'
				? FIREFOX_PATH
				: null;

	async ngOnInit(): Promise<void> {
		const isNative = Capacitor.isNativePlatform();

		if (isNative || !this.pluginPath) {
			// plugin not allowed here.
			this.hasPlugin$$.next(true);
		} else {
			this.bookmarksService.load().then((res) => {
				this.hasPlugin$$.next(res.state !== BookmarksState.PLUGIN_NOT_INSTALLED);
			});
		}
	}

	public async toggleCollapsedStatus(): Promise<void> {
		const isDesktop = await firstValueFrom(this.isDesktop$);
		const opened = await firstValueFrom(this.homeService.sidenavOpened$);
		isDesktop
			? this.sidenavService.toggleCollapsedStatus()
			: this.homeService.setSidenavOpened(!opened);
	}

	public async redirectToHome(): Promise<void> {
		await this.router.navigate(['/home/dashboard']);
	}
}
